import React, { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Color } from "../../../constants/color";
import { DocumentData } from "../../../types";
import {
  downloadCertificate,
  downloadCertificatesZip,
  getCertificatesByEmployee,
} from "../../../features/certificate/utils";
import { nomeCorso } from "../../../features/certificate/pipeline";
import { getCourseCatalog } from "../../../features/course/utils";
import {
  BadgeStatoAttestato,
  ScaricaAttestato,
} from "../../attestati/riga-attestato";
import Button from "../../button/button";
import Suggerimento from "../../tooltip/suggerimento";

/*
 Gli attestati del dipendente dentro la sua scheda di anagrafica.

 Li mostra TUTTI, anche quando il dipendente è cessato: elenco, conteggi e dashboard i suoi
 attestati li tolgono di mezzo, perché la conformità si misura su chi lavora in azienda oggi,
 e questa sezione è l'unico posto da cui restano visibili e scaricabili. La regola è del
 backend (ADR-0016): qui non si filtra e non si ricalcola nulla, si mostra quello che arriva,
 nell'ordine in cui arriva.

 Stato e download riusano il modulo della riga attestato, così una riga qui e una riga nella
 pagina attestati dicono la stessa cosa con gli stessi colori.

 Si possono spuntare più attestati e scaricarli insieme: esce un unico archivio zip, che
 compone il backend (GET /certificates/zip). Non è una libreria di compressione nel browser né
 una raffica di download separati — quella il browser la ostacola e sparpaglia i file nella
 cartella Download.
*/

const Sezione = styled.div`
  margin-top: 20px;
`;

/*
 Le colonne dell'elenco, una sola volta: spunta, corso, stato, download. La riga di comando
 sopra le righe usa la stessa griglia, altrimenti la spunta del «seleziona tutti» non
 sarebbe incolonnata con quelle che comanda.

 La colonna dello stato è larga perché ci sta la frase intera («SUPERATO, SCADEVA IL» più la
 data) su una riga sola: incolonnata com'è nelle tabelle attestati, qui si leggeva a fatica.
*/
const GRIGLIA_RIGA = css`
  display: grid;
  grid-template-columns: auto 1fr minmax(16rem, auto) auto;
  align-items: center;
  gap: 1rem;
`;

const Titolo = styled.h3`
  font-size: 1.2rem;
  font-weight: bolder;
  line-height: 1em;
  color: ${Color.LIGHT_BLUE};
  margin-bottom: 0.8rem;
`;

const Elenco = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

/*
 La riga di comando dell'elenco: la spunta sta nella stessa colonna di quelle delle righe —
 stessa griglia, stessa spaziatura interna — perche' si legga come l'intestazione di una
 tabella e non come una casella appoggiata li' accanto al titolo. Il pulsante sta a destra,
 dalla parte delle icone di download che comanda.
*/
const ComandiElenco = styled.div`
  ${GRIGLIA_RIGA}
  padding: 0 1rem 0.4rem 1rem;
  border-bottom: 1px solid ${Color.LIGHT_GREY};
  margin-bottom: 0.5rem;
`;

const SelezioneTutti = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  color: ${Color.DARK_GREY};
  font-size: 0.9rem;
  text-transform: uppercase;
`;

/* Occupa le due colonne di destra, quella dello stato e quella del download. */
const ComandiADestra = styled.div`
  grid-column: 3 / span 2;
  display: flex;
  justify-content: flex-end;
`;

const Riga = styled.div`
  ${GRIGLIA_RIGA}
  border: 1px solid ${Color.LIGHT_BLUE};
  border-radius: 1rem;
  padding: 0.5rem 1rem;
`;

const Corso = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const CodiceCorso = styled.span`
  color: ${Color.DARK_GREY};
  font-size: 0.9rem;
`;

const Spunta = styled.input`
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Messaggio = styled.p`
  color: ${Color.DARK_GREY};
`;

export default function AttestatiDipendente({
  employeeId,
}: {
  employeeId: number;
}) {
  const { t } = useTranslation();
  const [attestati, setAttestati] = useState<DocumentData[]>([]);
  const [catalogo, setCatalogo] = useState<Map<string, string>>(new Map());
  const [caricamento, setCaricamento] = useState(true);
  const [selezionati, setSelezionati] = useState<number[]>([]);
  const [scaricamentoInCorso, setScaricamentoInCorso] = useState(false);

  useEffect(() => {
    let annullato = false;
    setCaricamento(true);
    setSelezionati([]);
    // Il catalogo arriva insieme agli attestati: la riga chiama il corso col nome a
    // catalogo (`nomeCorso`), come le tab della pagina attestati. L'anagrafica sta fuori
    // dal loro CertificatesDatasetProvider, quindi lo chiede da sé.
    void Promise.all([
      getCertificatesByEmployee(employeeId),
      getCourseCatalog(),
    ]).then(([lista, catalogoCorsi]) => {
      // La modale può essere chiusa, o passare a un altro dipendente, mentre la
      // risposta è ancora in volo: senza questa guardia scriverebbe l'elenco di
      // qualcun altro.
      if (annullato) return;
      setAttestati(lista);
      setCatalogo(catalogoCorsi);
      setCaricamento(false);
    });
    return () => {
      annullato = true;
    };
  }, [employeeId]);

  // Un attestato senza file archiviato non si scarica, quindi non si spunta: la
  // riga resta, con la sua scadenza, ma la casella è spenta.
  const scaricabili = useMemo(
    () => attestati.filter((attestato) => Boolean(attestato.fileName)),
    [attestati]
  );

  const tuttiSelezionati =
    scaricabili.length > 0 && selezionati.length === scaricabili.length;

  function cambiaSelezioneTutti() {
    setSelezionati(
      tuttiSelezionati ? [] : scaricabili.map((attestato) => attestato.id)
    );
  }

  function cambiaSelezione(id: number) {
    setSelezionati((precedenti) =>
      precedenti.includes(id)
        ? precedenti.filter((selezionato) => selezionato !== id)
        : [...precedenti, id]
    );
  }

  /*
   Un unico archivio zip, preparato dal backend, non una raffica di download: il browser la
   ostacola, e i file finirebbero sparpagliati nella cartella Download di chi li sta
   raccogliendo per un'ispezione.

   Con un attestato solo l'archivio non serve: si scarica il PDF, che è quello che chi lo ha
   spuntato si aspetta di aprire con un doppio clic.
  */
  async function scaricaSelezionati() {
    const daScaricare = scaricabili.filter((attestato) =>
      selezionati.includes(attestato.id)
    );
    setScaricamentoInCorso(true);
    try {
      await (daScaricare.length === 1
        ? downloadCertificate(daScaricare[0])
        : downloadCertificatesZip(daScaricare));
    } finally {
      setScaricamentoInCorso(false);
    }
  }

  return (
    <Sezione>
      <Titolo>{t("attestati-dipendente-titolo")}</Titolo>
      {caricamento && (
        <Messaggio>{t("attestati-dipendente-caricamento")}</Messaggio>
      )}
      {!caricamento && attestati.length === 0 && (
        <Messaggio>{t("attestati-dipendente-nessuno")}</Messaggio>
      )}
      {!caricamento && scaricabili.length > 0 && (
        <ComandiElenco>
          <Spunta
            type="checkbox"
            id="attestati-dipendente-tutti"
            checked={tuttiSelezionati}
            onChange={cambiaSelezioneTutti}
          />
          <SelezioneTutti htmlFor="attestati-dipendente-tutti">
            {t("attestati-dipendente-seleziona-tutti")}
          </SelezioneTutti>
          <ComandiADestra>
            <Suggerimento testo={t("employee-modal-aiuto-scarica-selezionati")}>
              <Button
                type="button"
                label={t("attestati-dipendente-scarica-selezionati", {
                  numero: selezionati.length,
                })}
                color={Color.LIGHT_BLUE}
                fontSize={"14px"}
                disabled={selezionati.length === 0 || scaricamentoInCorso}
                onClick={() => {
                  void scaricaSelezionati();
                }}
              />
            </Suggerimento>
          </ComandiADestra>
        </ComandiElenco>
      )}
      {!caricamento && attestati.length > 0 && (
        <Elenco>
          {attestati.map((attestato) => (
            <Riga key={attestato.id}>
              <Spunta
                type="checkbox"
                aria-label={nomeCorso(attestato, catalogo)}
                checked={selezionati.includes(attestato.id)}
                disabled={!attestato.fileName}
                onChange={() => cambiaSelezione(attestato.id)}
              />
              <Corso>
                <span>{nomeCorso(attestato, catalogo)}</span>
                <CodiceCorso>{attestato.metadata.codice_corso}</CodiceCorso>
              </Corso>
              <BadgeStatoAttestato attestato={attestato} inline />
              <ScaricaAttestato attestato={attestato} />
            </Riga>
          ))}
        </Elenco>
      )}
    </Sezione>
  );
}
